import { OverdueTicketsByEmployee } from '@/modules/Ticket/types/response'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'

interface TicketOverDuedateChartProps {
    overdue_tickets_by_employee?: OverdueTicketsByEmployee[]
}

const COLORS = {
    overdueNotProcessed: '#EF5350',
    overdueProcessed: '#1A237E',
    slaOnTime: '#4CAF50',
    ticketNotDue: '#FF9800',
}

const TicketOverDuedateChart: React.FC<TicketOverDuedateChartProps> = ({
    overdue_tickets_by_employee = [],
}) => {
    const { t } = useTranslation()

    const chartData = useMemo(() => {
        return overdue_tickets_by_employee.map((item) => {
            // Tickets not due = total - all overdue categories
            const overdueSum =
                item.overdue_tickets +
                item.overdue_tickets_closed +
                item.overdue_tickets_closed_on_time
            const ticketNotDue = Math.max(0, item.total_tickets - overdueSum)

            return {
                name: item.employee_name,
                overdueNotProcessed: item.overdue_tickets,
                overdueProcessed: item.overdue_tickets_closed,
                slaOnTime: item.overdue_tickets_closed_on_time,
                ticketNotDue,
            }
        })
    }, [overdue_tickets_by_employee])

    const seriesConfig = [
        {
            dataKey: 'overdueNotProcessed',
            name: t('overdue-not-processed'),
            color: COLORS.overdueNotProcessed,
        },
        {
            dataKey: 'overdueProcessed',
            name: t('overdue-processed'),
            color: COLORS.overdueProcessed,
        },
        {
            dataKey: 'slaOnTime',
            name: t('sla-on-time'),
            color: COLORS.slaOnTime,
        },
        {
            dataKey: 'ticketNotDue',
            name: t('ticket-not-due'),
            color: COLORS.ticketNotDue,
        },
    ]

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null

        const total = payload.reduce(
            (sum: number, entry: any) => sum + (entry.value || 0),
            0
        )

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800 mb-2">
                    {label} ({t('total')}: {total})
                </p>
                <div className="space-y-1 text-sm">
                    {payload.map((entry: any, index: number) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}:{' '}
                            <span className="font-medium">{entry.value}</span>
                        </p>
                    ))}
                </div>
            </div>
        )
    }

    // Custom label renderer for values inside bars
    const renderInsideLabel = (props: any) => {
        const { x, y, width, height, value } = props
        if (!value || value === 0) return null

        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#fff"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={10}
            >
                {value}
            </text>
        )
    }

    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-9 col-span-12 flex flex-col half-col-print">
            <Typogaphy size="small" className="text-primary font-semibold">
                {t('ticket-processing-info')} (
                {overdue_tickets_by_employee?.reduce(
                    (acc, item) => acc + item.total_tickets,
                    0
                )}
                )
            </Typogaphy>

            <div className="flex-1 flex items-center justify-center w-full min-h-[300px]">
                {overdue_tickets_by_employee.length === 0 ? (
                    <Typogaphy className="text-gray-500 text-center text-sm">
                        {t('no-data')}
                    </Typogaphy>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 20,
                                right: 10,
                                left: -20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="name"
                                stroke="#9ca3af"
                                fontSize={10}
                                tickLine={false}
                                angle={chartData.length > 5 ? -15 : 0}
                                textAnchor={
                                    chartData.length > 5 ? 'end' : 'middle'
                                }
                                height={chartData.length > 5 ? 50 : 30}
                            />
                            <YAxis
                                stroke="#9ca3af"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                verticalAlign="top"
                                height={36}
                                content={({ payload }) => (
                                    <div className="flex justify-center gap-4 flex-wrap">
                                        {payload?.map((entry, index) => (
                                            <div
                                                key={`legend-${index}`}
                                                className="flex items-center gap-1.5"
                                            >
                                                <div
                                                    className="w-3 h-3 rounded-sm"
                                                    style={{
                                                        backgroundColor:
                                                            entry.color,
                                                    }}
                                                />
                                                <span className="text-xs text-gray-600">
                                                    {entry.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                            {seriesConfig.map((series, index) => (
                                <Bar
                                    key={series.dataKey}
                                    dataKey={series.dataKey}
                                    name={series.name}
                                    stackId="stack"
                                    fill={series.color}
                                    barSize={30}
                                    radius={
                                        index === seriesConfig.length - 1
                                            ? [4, 4, 0, 0]
                                            : [0, 0, 0, 0]
                                    }
                                >
                                    <LabelList
                                        dataKey={series.dataKey}
                                        content={renderInsideLabel}
                                    />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    )
}

export default TicketOverDuedateChart
